import { produce } from 'immer';
import _ from 'lodash';
import {
  MutableRefObject,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { HttpResponse } from '../../../../../common/ajax';
import {
  hasValidationErrors,
  setValidationErrors,
  showErrorToast,
  showSuccessToast,
} from '../../../../../common/global/helpers';
import Button from '../../../../../common/widgets/Button';
import Dropdown from '../../../../../common/widgets/Dropdown';
import ErrorViewer from '../../../../../common/widgets/ErrorViewer';
import Icons from '../../../../../common/widgets/Icons';
import Input from '../../../../../common/widgets/Input';
import {
  Notification,
  NotificationType,
} from '../../../../../common/widgets/Notification';
import { paymentMethodCodes } from '../../../constants';
import { CustomerInfoContext } from '../../../contexts/CustomerInfoContext';
import { PlatformInfoContext } from '../../../contexts/PlatformInfoContext';
import { BudgetTypes, CustomClientTypes, ProductTypes } from '../../../enums';
import { dispatchShowCartDropdownEvent } from '../../../helpers';
import { Dictionary } from '../../../models/Categories';
import {
  CheckOutState,
  CheckoutVoucherResponseModel,
} from '../../../models/Checkout';
import {
  ErrorType,
  NotFoundObjectError,
  ValidationError,
} from '../../../models/Errors';
import { DependentModel } from '../../../models/Options';
import { PaymentMethodModel } from '../../../models/PaymentMethods';
import { PriceModel } from '../../../models/Product';
import { ProductPaymentMethodDetailsModel } from '../../../models/ProductPaymentMethodDetailsModel';
import { resources } from '../../../resources/resources';
import { productsService } from '../../../services/products';
import { voucherService } from '../../../services/vouchers';
import { wishlistService } from '../../../services/wishlist';
import {
  OptionsForm,
  buildInitialForm,
  getPriceChangingOptions,
} from '../../Common/Options';
import {
  ProductDetailsFormProps,
  getActionButtonLabel,
  getProductFormAction,
} from './common';
import CustomDependentSelector from './CustomDependentSelector';
import DiscountAndVoucherInfo from './DiscountAndVoucherInfo';
import PriceAndTaxRateInfo from './PriceAndTaxRateInfo';
import { ProductPaymentMethods } from './ProductPaymentMethods';

const shouldShowPriceInfo = (
  hidePriceAndTaxes: boolean,
  hasPriceRange: boolean,
): boolean => {
  if (hidePriceAndTaxes) {
    return hasPriceRange;
  }
  return true;
};

const shouldShowDiscountInfo = (productTypeId: ProductTypes): boolean =>
  productTypeId !== ProductTypes.DefaultProduct;

const isFreeOrContactVoucherType = (productTypeId: ProductTypes): boolean =>
  productTypeId === ProductTypes.FreePdfVoucher ||
  productTypeId === ProductTypes.FreeLinkVoucher ||
  productTypeId === ProductTypes.CashBack ||
  productTypeId === ProductTypes.ContactVoucher;

const isRecurringProductErrorState = (
  isRecurring: boolean,
  paymentMethodDetails: ProductPaymentMethodDetailsModel[],
): boolean => {
  if (!isRecurring || paymentMethodDetails.length !== 1) return false;
  const firstPaymentMethod = paymentMethodDetails[0];
  return (
    firstPaymentMethod.budgetType === BudgetTypes.BenefitBudgetOnly &&
    firstPaymentMethod.error !== null
  );
};

const isBenefitOnlyPaymentErrorState = (
  paymentMethods: PaymentMethodModel[],
  paymentMethodDetails: ProductPaymentMethodDetailsModel[],
): boolean => {
  const cardCodes = [
    paymentMethodCodes.mobilpay,
    paymentMethodCodes.mobilpaygv,
    paymentMethodCodes.mobilpaycv,
  ];

  const codes = new Set(paymentMethods?.map(pm => pm.code));

  const hasCardPayment = cardCodes.some(code => codes.has(code));
  const hasBenefitBudget = codes.has(paymentMethodCodes.benefitBudget);

  if (hasCardPayment || !hasBenefitBudget) return false;
  if (paymentMethodDetails.length !== 1) return false;

  const [firstPaymentMethod] = paymentMethodDetails;
  return (
    firstPaymentMethod.budgetType === BudgetTypes.BenefitBudgetOnly &&
    firstPaymentMethod.error !== null
  );
};

interface SubmitButtonState {
  isRecurringProductError: boolean;
  isFreeOrContactVoucher: boolean;
  isClosedDefaultProduct: boolean;
  selectedCheckoutMethod: BudgetTypes | undefined;
  showNoCodes: boolean;
  showTemporaryDeactivate: boolean;
  isMissingHpeDependents: boolean;
  canElectNow: boolean;
  isBenefitOnlyPaymentError: boolean;
}

const isSubmitButtonDisabled = (state: SubmitButtonState): boolean => {
  if (state.isRecurringProductError) return true;
  if (state.isFreeOrContactVoucher) return false;
  if (state.isBenefitOnlyPaymentError) return true;
  return (
    state.isClosedDefaultProduct ||
    !state.selectedCheckoutMethod ||
    state.showNoCodes ||
    state.showTemporaryDeactivate ||
    state.isMissingHpeDependents ||
    !state.canElectNow
  );
};

export default function ProductDetailsForm(props: ProductDetailsFormProps) {
  const { t: translate } = useTranslation();
  const navigate = useNavigate();

  const calculatePriceAbortControllerRef = useRef<AbortController | null>(null);
  const calculatePriceDebounceTimeoutRef = useRef<ReturnType<
    typeof setTimeout
  > | null>(null);
  const adjustedCashbackDiscountRef = useRef<number | null>(null);
  const previousSelectedOptionsRef = useRef<Dictionary<any>>({});

  const isFreeOrContactVoucher = isFreeOrContactVoucherType(
    props.productTypeId,
  );

  const { customerInfo, setCustomerInfo, loadCustomerInfo } =
    useContext(CustomerInfoContext);
  const platformInfo = useContext(PlatformInfoContext);

  const [form, setForm] = useState<Dictionary<any>>(
    buildInitialForm(
      props.options,
      props.recurrences,
      translate,
      props.dependentsConfig?.dependents
        ? props.dependentsConfig.dependents
        : ([] as DependentModel[]),
    ),
  );
  const [price, setPrice] = useState<PriceModel>({
    defaultPrice: props.defaultPrice,
    discountedPrice: props.discountedPrice,
    priceRanges: props.priceRanges,
    priceOptionId: props.priceOptionId,
  });

  const [paymentMethodDetails, setPaymentMethodDetails] = useState<
    ProductPaymentMethodDetailsModel[]
  >(props.paymentMethodDetails ?? []);

  const isRecurringProductError = isRecurringProductErrorState(
    props.isRecurring,
    paymentMethodDetails,
  );

  const isBenefitOnlyPaymentError = isBenefitOnlyPaymentErrorState(
    props.paymentMethods ?? [],
    paymentMethodDetails,
  );

  const [selectedCheckoutMethod, setSelectedCheckoutMethod] = useState<
    BudgetTypes | undefined
  >();
  const [isSalaryDeductionSelected, setIsSalaryDeductionSelected] =
    useState<boolean>(false);
  const [formResetKey, setFormResetKey] = useState<number>(0);
  const [cashbackDiscount, setCashbackDiscount] = useState<number>(0);
  const [softLimitMessage, setSoftLimitMessage] = useState<string | undefined>(
    props.softLimitMessage,
  );
  const [isPriceLoading, setIsPriceLoading] = useState<boolean>(false);
  const [isSaveButtonLoading, setIsSaveButtonLoading] =
    useState<boolean>(false);
  const addFavoriteAbortControllerRef: MutableRefObject<AbortController> =
    useRef<AbortController>(new AbortController());
  const removeFavoriteAbortControllerRef: MutableRefObject<AbortController> =
    useRef<AbortController>(new AbortController());
  const isSubmittingRef = useRef(false);
  const isFavoriteActionRef = useRef(false);

  const calculatePrice = useCallback(
    async (options: Dictionary<any>) => {
      calculatePriceAbortControllerRef.current?.abort();

      const controller = new AbortController();
      calculatePriceAbortControllerRef.current = controller;
      setIsPriceLoading(true);
      try {
        const result = await productsService.calculatePrice(
          props.productId,
          1,
          options,
          cashbackDiscount,
          { signal: controller.signal },
        );

        if (controller.signal.aborted) {
          return;
        }
        if (result.data) {
          setPrice({
            defaultPrice: result.data.defaultPrice,
            discountedPrice: result.data.discountedPrice,
            priceRanges: props.priceRanges,
            priceOptionId: props.priceOptionId,
          });
          setSoftLimitMessage(result.data.softLimitMessage);
          if (
            cashbackDiscount > 0 &&
            result.data.adjustedCashbackDiscount > 0 &&
            result.data.adjustedCashbackDiscount !== cashbackDiscount
          ) {
            adjustedCashbackDiscountRef.current =
              result.data.adjustedCashbackDiscount;
            setCashbackDiscount(result.data.adjustedCashbackDiscount);
          }
          // Update payment method details if available in the response
          setPaymentMethodDetails(result.data.paymentMethodDetails || []);
        }
      } catch (error) {
        console.error('Price calculation error:', error);
      } finally {
        setIsPriceLoading(false);
      }
    },
    [props.productId, props.priceRanges, props.priceOptionId, cashbackDiscount],
  );

  useEffect(() => {
    const newSelectedOptions = getPriceChangingOptions(form, props.options);
    if (_.isEqual(previousSelectedOptionsRef.current, newSelectedOptions)) {
      return;
    }
    previousSelectedOptionsRef.current = newSelectedOptions;

    if (calculatePriceDebounceTimeoutRef.current) {
      globalThis.clearTimeout(calculatePriceDebounceTimeoutRef.current);
    }

    calculatePriceDebounceTimeoutRef.current = globalThis.setTimeout(() => {
      calculatePrice(newSelectedOptions);
    }, 100);

    return () => {
      if (calculatePriceDebounceTimeoutRef.current) {
        globalThis.clearTimeout(calculatePriceDebounceTimeoutRef.current);
      }
    };
  }, [form, props.options, calculatePrice]);

  useEffect(() => {
    const adjustedCashbackDiscount = adjustedCashbackDiscountRef.current;
    adjustedCashbackDiscountRef.current = null;
    if (adjustedCashbackDiscount === cashbackDiscount) {
      return;
    }

    const newSelectedOptions = getPriceChangingOptions(form, props.options);

    if (calculatePriceDebounceTimeoutRef.current) {
      globalThis.clearTimeout(calculatePriceDebounceTimeoutRef.current);
    }

    calculatePriceDebounceTimeoutRef.current = globalThis.setTimeout(() => {
      calculatePrice(newSelectedOptions);
    }, 100);

    return () => {
      if (calculatePriceDebounceTimeoutRef.current) {
        globalThis.clearTimeout(calculatePriceDebounceTimeoutRef.current);
      }
    };
  }, [cashbackDiscount, form, props.options, calculatePrice]);

  useEffect(() => {
    return () => {
      if (calculatePriceDebounceTimeoutRef.current) {
        globalThis.clearTimeout(calculatePriceDebounceTimeoutRef.current);
      }

      calculatePriceAbortControllerRef.current?.abort();
    };
  }, []);

  const [isFavorite, setIsFavorite] = useState(props.isFavorite);
  const [isFavoriteActionRunning, setIsFavoriteActionRunning] = useState(false);

  const addToFavorties = async (e: MouseEvent) => {
    e.preventDefault();
    if (isFavoriteActionRef.current) return;
    isFavoriteActionRef.current = true;
    setIsFavoriteActionRunning(true);

    setIsFavorite(true);
    setCustomerInfo(
      produce(customerInfo, d => {
        d.favoriteProductsCount += 1;
      }),
    );

    addFavoriteAbortControllerRef.current = new AbortController();
    removeFavoriteAbortControllerRef.current.abort();

    try {
      const resp = await wishlistService.addProduct(props.productId, {
        signal: addFavoriteAbortControllerRef.current.signal,
      });
      if (addFavoriteAbortControllerRef.current.signal.aborted) {
        return;
      }
      if (resp.hasErrors) {
        setIsFavorite(false);
        setCustomerInfo(
          produce(customerInfo, d => {
            d.favoriteProductsCount -= 1;
          }),
        );
        showErrorToast(translate(resources.errors.generic));
      }
    } finally {
      isFavoriteActionRef.current = false;
      setIsFavoriteActionRunning(false);
    }
  };

  const removeFromFavorties = async (e: MouseEvent) => {
    e.preventDefault();
    if (isFavoriteActionRef.current) return;
    isFavoriteActionRef.current = true;
    setIsFavoriteActionRunning(true);

    setIsFavorite(false);
    setCustomerInfo(
      produce(customerInfo, d => {
        d.favoriteProductsCount -= 1;
      }),
    );

    removeFavoriteAbortControllerRef.current = new AbortController();
    addFavoriteAbortControllerRef.current.abort();

    try {
      const resp = await wishlistService.removeProduct(props.productId, {
        signal: removeFavoriteAbortControllerRef.current.signal,
      });
      if (removeFavoriteAbortControllerRef.current.signal.aborted) {
        return;
      }
      if (resp.hasErrors) {
        setIsFavorite(true);
        setCustomerInfo(
          produce(customerInfo, d => {
            d.favoriteProductsCount += 1;
          }),
        );
        showErrorToast(translate(resources.errors.generic));
      }
    } finally {
      isFavoriteActionRef.current = false;
      setIsFavoriteActionRunning(false);
    }
  };

  const handleFormActionClick = async () => {
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;

    setIsSaveButtonLoading(true);
    try {
      await getProductFormAction({
        productId: props.productId,
        productTypeId: props.productTypeId,
        form,
        cashbackDiscount,
        options: props.options,
        link: props.url,
        selectedCheckoutMethod,
        isSalaryDeductionSelected,
        handlers: {
          handleAddToCartResponse,
          handleContactResponse,
          handleVoucherCheckoutResponse,
          handleRedirectResponse,
          handleCashbackResponse,
        },
      });
    } finally {
      isSubmittingRef.current = false;
    }
  };

  const handleAddToCartResponse = async (response: HttpResponse<any>) => {
    setIsSaveButtonLoading(false);
    if (response.hasErrors) {
      // Recalculate price in case anything budget-related changed
      const currentSelectedOptions = getPriceChangingOptions(
        form,
        props.options,
      );
      await calculatePrice(currentSelectedOptions);

      if (response.errorType === ErrorType.ValidationError) {
        setValidationErrors(response, props.setErrors);
      } else if (
        response.errorType === ErrorType.NotFoundError &&
        (response.error as NotFoundObjectError).errorMessage ===
          'Shop not found'
      ) {
        showErrorToast(translate(resources.products.shopNotFound));
      } else {
        showErrorToast(translate(resources.errors.generic));
      }
    } else {
      const newCartItems = response.data;

      dispatchShowCartDropdownEvent(newCartItems);

      setCustomerInfo(
        produce(customerInfo, draft => {
          draft.cartItemsCount = newCartItems.length;
        }),
      );

      // Reset the form (options, payment method, uploaded files) so the
      // user has to fill it in again for the next add-to-cart
      const resetForm = buildInitialForm(
        props.options,
        props.recurrences,
        translate,
        props.dependentsConfig?.dependents ?? ([] as DependentModel[]),
      );
      setForm(resetForm);
      setSelectedCheckoutMethod(undefined);
      setIsSalaryDeductionSelected(false);
      setFormResetKey(key => key + 1);
      if (
        props.customerBudget.benefitBucksBalance &&
        props.customerBudget.benefitBucksBalance >= cashbackDiscount
      ) {
        props.updateCustomerBudgetCashback(
          props.customerBudget.benefitBucksBalance - cashbackDiscount,
        );
      }
      setCashbackDiscount(0);

      // Recalculate price with the reset/default options, now that the
      // form has been cleared, instead of the just-submitted selection
      await calculatePrice(getPriceChangingOptions(resetForm, props.options));
    }
  };

  const handleVoucherCheckoutResponse = async (
    response: HttpResponse<CheckoutVoucherResponseModel>,
  ) => {
    if (response.hasErrors || response.data.state !== CheckOutState.Placed) {
      setIsSaveButtonLoading(false);
      if (response.errorType === ErrorType.ValidationError) {
        setValidationErrors(response, props.setErrors);
      } else {
        showErrorToast(translate(resources.errors.generic));
      }
    } else {
      setIsSaveButtonLoading(true);
      showSuccessToast(translate(resources.vouchers.checkoutSuccess));
      loadCustomerInfo();
      const resp = await voucherService.downloadVoucher(
        props.productId,
        response.data.orderId!,
        response.data.codeType!,
        response.data.codeIntId!,
        null,
        props.productName ?? '',
      );
      setIsSaveButtonLoading(false);
      if (resp.hasErrors) {
        showErrorToast(translate(resources.errors.generic));
      }
    }
  };

  const handleRedirectResponse = () => {
    setIsSaveButtonLoading(false);
  };

  const handleCashbackResponse = () => {
    setIsSaveButtonLoading(false);
  };

  const handleContactResponse = (response: HttpResponse<any>) => {
    setIsSaveButtonLoading(false);

    if (!response.hasErrors) {
      navigate('/cart-success');
      loadCustomerInfo();
      return;
    }

    if (response.errorType === ErrorType.ValidationError) {
      setValidationErrors(response, props.setErrors);
      return;
    }

    showErrorToast(translate(resources.errors.generic));
  };

  const clearErrors = (optionId: number | string) => {
    props.setErrors(
      produce(draft => {
        if (draft) {
          draft[optionId] = [];
        }
      }),
    );
  };

  const priceOption = props.priceOptionId
    ? props.options.find(o => o.id === props.priceOptionId)
    : undefined;

  const nonPriceOptions = props.priceOptionId
    ? props.options.filter(o => o.id !== props.priceOptionId)
    : props.options;

  const hasPriceRange =
    !_.isNil(price.priceRanges) && price.priceRanges.length >= 2;

  const isClosedDefaultProduct =
    props.productTypeId === ProductTypes.DefaultProduct &&
    !platformInfo?.isPlatformOpened &&
    !props.isPayableByCard;

  const isMissingHpeDependents =
    props.customClientType === CustomClientTypes.HpeCustom &&
    props.dependentsConfig === null;

  const isSubmitDisabled = isSubmitButtonDisabled({
    isRecurringProductError,
    isFreeOrContactVoucher,
    isClosedDefaultProduct,
    selectedCheckoutMethod,
    showNoCodes: props.showNoCodes,
    showTemporaryDeactivate: props.showTemporaryDeactivate,
    isMissingHpeDependents,
    canElectNow: props.canElectNow,
    isBenefitOnlyPaymentError,
  });

  return (
    <div className="product-add-cart">
      {shouldShowPriceInfo(props.hidePriceAndTaxes, hasPriceRange) ? (
        <PriceAndTaxRateInfo
          price={price}
          taxRate={props.taxRate}
          taxInformations={props.taxInformation}
          isPriceLoading={isPriceLoading}
          isRecurring={props.isRecurring}
          hasPriceRange={hasPriceRange}
          cashback={cashbackDiscount}
        ></PriceAndTaxRateInfo>
      ) : (
        <></>
      )}
      {shouldShowDiscountInfo(props.productTypeId) && (
        <DiscountAndVoucherInfo
          voucherType={props.productTypeId}
          discount={props.voucherDiscount}
          voucherCode={props.voucherCode}
          voucherUrl={props.url}
        ></DiscountAndVoucherInfo>
      )}

      {priceOption &&
        props.customClientType !== CustomClientTypes.HpeCustom && (
          <div className="products-options-container">
            <OptionsForm
              key={formResetKey}
              options={[priceOption]}
              form={form}
              setForm={setForm}
              errors={props.errors}
              setErrors={props.setErrors}
              clearErrors={clearErrors}
              productId={props.productId}
            />
          </div>
        )}

      <ProductPaymentMethods
        productPaymentMethods={paymentMethodDetails}
        paymentMethods={props.paymentMethods}
        selectedCheckoutMethod={selectedCheckoutMethod}
        setSelectedCheckoutMethod={setSelectedCheckoutMethod}
        setIsSalaryDeductionSelected={setIsSalaryDeductionSelected}
        isRecurring={props.isRecurring}
        setPaymentError={props.setPaymentError}
        hasPriceRange={hasPriceRange}
        customerBudget={props.customerBudget}
        cashbackDiscount={cashbackDiscount}
        setCashbackDiscount={(value: number) => setCashbackDiscount(value)}
        productPrice={price.discountedPrice ?? price.defaultPrice}
        isPayableByCashback={props.isPayableByCashback}
      />

      {/* Product options */}
      {(nonPriceOptions.length > 0 || props.recurrences?.length > 0) && (
        <div className="products-options-container">
          {props.recurrences?.length > 0 && (
            <>
              {props.recurrences.length > 1 ? (
                <Dropdown
                  data={props.recurrences}
                  dataItemKey="id"
                  textField="title"
                  value={form['recurrenceId']}
                  label={`*${translate(resources.products.recurrencePeriod)}`}
                  onChange={e => {
                    setForm(
                      produce(draft => {
                        draft['recurrenceId'] = e.value;
                      }),
                    );
                  }}
                  errors={props.errors.recurrenceId}
                  clearErrors={() => clearErrors('recurrenceId')}
                />
              ) : (
                <Input
                  label={translate(resources.products.recurrencePeriod)}
                  value={props.recurrences[0].title}
                  disabled
                />
              )}
            </>
          )}
          {props.customClientType === CustomClientTypes.HpeCustom ? (
            <div className="products-options-container">
              <h4>{translate(resources.products.hpe.chooseDependents)}</h4>
              {props.dependentsConfig &&
              props.dependentsConfig.dependents.length > 0 ? (
                <CustomDependentSelector
                  dependentsConfig={props.dependentsConfig}
                  form={form}
                  setForm={setForm}
                  errors={props.errors}
                  clearErrors={clearErrors}
                />
              ) : (
                <ErrorViewer
                  errors={[
                    {
                      message: translate(resources.cart.errors.hpe.noDependent),
                      severity: 'error',
                    } as ValidationError,
                  ]}
                ></ErrorViewer>
              )}
            </div>
          ) : (
            <OptionsForm
              key={formResetKey}
              options={nonPriceOptions}
              form={form}
              setForm={setForm}
              errors={props.errors}
              setErrors={props.setErrors}
              clearErrors={clearErrors}
              productId={props.productId}
            />
          )}
          {hasValidationErrors(_.omit(props.errors, 'null')) && (
            <Notification
              type={NotificationType.Error}
              messages={[
                { message: translate(resources.products.optionsError) },
              ]}
              scrollUp={false}
            />
          )}
          {!!softLimitMessage && (
            <Notification
              type={NotificationType.Info}
              messages={[{ message: softLimitMessage }]}
              scrollUp={false}
            />
          )}
          {props.showNoCodes && (
            <Notification
              type={NotificationType.Info}
              messages={[
                { message: translate(resources.products.errorNoCodes) },
              ]}
              scrollUp={false}
            />
          )}
          {props.showTemporaryDeactivate && (
            <Notification
              type={NotificationType.Info}
              messages={[
                {
                  message: translate(resources.products.platformClosed.warning),
                },
              ]}
              scrollUp={false}
            />
          )}
        </div>
      )}
      {props.productTypeId === ProductTypes.FreePdfVoucher &&
        props.options.length == 0 &&
        props.recurrences?.length == 0 &&
        props.showTemporaryDeactivate && (
          <Notification
            type={NotificationType.Info}
            messages={[
              {
                message: translate(resources.products.platformClosed.warning),
              },
            ]}
            scrollUp={false}
          />
        )}

      <div className="product-actions">
        <Button
          onClick={handleFormActionClick}
          className="btn btn-solid btn-lg btn-full btn-product-add"
          loading={isSaveButtonLoading}
          disabled={isSubmitDisabled}
        >
          <span className="btn-text">
            {props.customLabel
              ? props.customLabel
              : translate(getActionButtonLabel(props.productTypeId))}
          </span>
        </Button>
        <button
          onClick={(e: any) =>
            isFavorite ? removeFromFavorties(e) : addToFavorties(e)
          }
          className={`btn btn-product-favorite ${isFavorite ? 'active' : ''}`}
          disabled={isFavoriteActionRunning}
        >
          <span className="btn-icon">
            <Icons.CardFavorite />
          </span>
        </button>
      </div>
    </div>
  );
}
